import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Eye } from 'lucide-react';
import { Product } from '../types';
import { getProductImageSrc } from '../utils/images';
import { useCartStore } from '../store/cartStore';
import { useState } from 'react';

interface ProductGridProps {
  products: Product[];
  columns?: 1 | 2 | 3 | 4 | 5;
}

export const ProductGrid = ({ products, columns = 4 }: ProductGridProps) => {
  const { addItem } = useCartStore();
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  };

  const handleAddToCart = (product: Product, size: string) => {
    addItem(product, size, 1);
  };

  const getDefaultSize = (product: Product) => {
    return product.sizes.length > 0 ? product.sizes[0] : 'One Size';
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-6`}>
      {products.map((product) => {
        const isHovered = hoveredProduct === product.id;
        const defaultSize = getDefaultSize(product);
        
        return (
          <div
            key={product.id}
            className="product-card group"
            onMouseEnter={() => setHoveredProduct(product.id)}
            onMouseLeave={() => setHoveredProduct(null)}
          >
            {/* Product Image */}
            <div className="relative overflow-hidden">
              <Link to={`/product/${product.id}`}>
                <img
                  src={getProductImageSrc(product.image)}
                  alt={product.name}
                  className="product-image"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/placeholder.png';
                  }}
                />
              </Link>
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col space-y-2">
                {product.isNew && (
                  <span className="badge badge-default text-xs font-semibold">New</span>
                )}
                {product.isSale && (
                  <span className="badge badge-destructive text-xs font-semibold">Sale</span>
                )}
                {/* SKU Badge */}
                <span className="badge badge-secondary text-xs font-mono bg-gray-800 text-white">
                  SKU: {product.id}
                </span>
              </div>

              {/* Quick Actions */}
              <div className={`absolute top-4 right-4 flex flex-col space-y-3 transition-all duration-500 ${
                isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
              }`}>
                <button className="p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300 hover:scale-110">
                  <Heart className="h-5 w-5 text-charcoal-600" />
                </button>
                <Link
                  to={`/product/${product.id}`}
                  className="p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300 hover:scale-110"
                >
                  <Eye className="h-5 w-5 text-charcoal-600" />
                </Link>
              </div>

              {/* Add to Cart Button */}
              <div className={`absolute bottom-0 left-0 right-0 p-6 transition-all duration-500 ${
                isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}>
                <button
                  onClick={() => handleAddToCart(product, defaultSize)}
                  className="w-full btn-primary btn-md flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <ShoppingBag className="h-5 w-5" />
                  <span>Add to Cart</span>
                </button>
              </div>
            </div>

            {/* Product Info */}
            <div className="p-6">
              <Link to={`/product/${product.id}`}>
                <h3 className="font-semibold text-charcoal-900 mb-3 line-clamp-2 hover:text-primary-600 transition-colors leading-tight">
                  {product.name}
                </h3>
              </Link>
              
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-sm text-charcoal-500 capitalize font-medium">{product.category}</span>
                <span className="text-charcoal-300">•</span>
                <span className="text-sm text-charcoal-500 font-medium">{product.color}</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {product.isSale && product.discount ? (
                    <>
                      <span className="price-sale">₹{product.price}</span>
                      <span className="price-original">
                        ₹{Math.round(product.price / (1 - product.discount / 100))}
                      </span>
                    </>
                  ) : (
                    <span className="price-regular">₹{product.price}</span>
                  )}
                </div>
                
                <div className="flex items-center space-x-1">
                  <span className="text-sm text-gray-500">Size:</span>
                  <span className="text-sm font-medium text-gray-900">{defaultSize}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
